import { Fragment, useCallback, useState } from "react";
import TableComp from "src/components/shared/table/TableComp";
import useCustomerBody from "./utils/useCustomerBody";
import { Box, Button, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import useCustomerAction from "./utils/useCustomerAction";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "src/hooks/useDebounce";
import { COMMOM_STATUS, USER_TYPE } from "src/constant";
import SearchFilterComp from "src/components/shared/SearchFilterComp";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import useCustomerExport from "src/hooks/user/customer/useCustomerExport";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import appConfig from "src/config/env";
import { enqueueSnackbar } from "notistack";
import useCustomerImport from "src/hooks/user/customer/useCustomerImport";
import ExportIcon from "src/components/shared/icon/ExportIcon";
import { PERMISSIONS, useRoleCheck } from "src/hooks/permission/permission";

export default function CustomerTable() {
  const permissionCheck = useRoleCheck();
  const action = useCustomerAction();
  const [keyword, setKeyword] = useState();
  const [searchBy] = useState();
  const [saleSearch, setSaleSearch] = useState();
  const [logId, setLogId] = useState();
  const debouncedSale = useDebounce(saleSearch, 500);
  const debouncedKey = useDebounce(keyword, 500);
  const { trigger: exportTrigger } = useCustomerExport();
  const { trigger: importTrigger } = useCustomerImport();
  const { mutate } = useProcessLogDetail(logId);

  const {
    header,
    bodyList,
    sortData,
    total,
    mutate: customerListMutate,
    selectedIds,
  } = useCustomerBody({
    debouncedKey,
    searchBy,
    debouncedSale,
  });

const handleImport = useCallback(async () => {
  const fileElement = document.createElement("input");
  fileElement.type = "file";
  fileElement.accept = ".xlsx, .xls, .csv";
  fileElement.click();

  fileElement.addEventListener("change", async (event) => {
    const fileList = (event.target).files;

    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await importTrigger({ body: formData });

      setLogId(response?.data?._id);

      enqueueSnackbar("Tiến hành nhập danh sách khách hàng...", {
        variant: "success",
        autoHideDuration: 2000,
      });

      if (response?.success) {
        const timer = setInterval(async () => {
          const res = await mutate();

          if (res?.data?.status === COMMOM_STATUS.done) {
            customerListMutate();
            enqueueSnackbar("Nhập danh sách khách hàng thành công", {
              variant: "success",
            });
            clearInterval(timer);
          }

          if (res?.data?.status === COMMOM_STATUS.error) {
            clearInterval(timer);

            let message =
              "Validation hoặc ghi dữ liệu thất bại; không import một phần";

            try {
              const errors = JSON.parse(res?.data?.error ?? "[]");

              if (Array.isArray(errors) && errors.length > 0) {
                const preview = errors
                  .slice(0, 5)
                  .map(
                    (item: { row: number; error: string }) =>
                      `Dòng ${item.row}: ${item.error}`
                  )
                  .join(" | ");

                message =
                  preview +
                  (errors.length > 5
                    ? ` | Và ${errors.length - 5} lỗi khác.`
                    : "");
              }
            } catch {
              if (typeof res?.data?.error === "string") {
                message = res.data.error;
              }
            }

            enqueueSnackbar(message, {
              variant: "error",
              autoHideDuration: 10000,
            });
          }
        }, 1000);
      }
    } catch (error) {
      enqueueSnackbar("Có lỗi xảy ra trong quá trình tải lên", {
        variant: "error",
      });
    }
  });
}, [customerListMutate, importTrigger, mutate]);

  const handleExport = useCallback(async () => {
    try {
      const hasSelection = Array.isArray(selectedIds) && selectedIds.length > 0;
      const response = await exportTrigger({
        body: hasSelection ? { ids: selectedIds } : {},
      });

      if (response?.success) {
        setLogId(response?.data?._id);
        enqueueSnackbar("Tiến hành xuất danh sách khách hàng...", { variant: "success", className: 'notistack-confirm' });
        const timer = setInterval(async () => {
          const res = await mutate();
          if (res?.data?.status === COMMOM_STATUS.done) {
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timer);
          }
          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được danh sách", { variant: "error" });
            clearInterval(timer);
          }
        }, 1000);
      }
    } catch (error) {}
  }, [exportTrigger, mutate, selectedIds]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await fetch(`${appConfig.API_HOST}/api/v1/customers/import/template`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.click();
    } catch (error) {
      enqueueSnackbar("Có lỗi xảy ra trong quá trình tải mẫu", { variant: "error" });
    }
  }, []);

  return (
    <Fragment>
      <Stack
        gap={1.5}
      >
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
            <Box flex={1}>
              <SearchFilterComp
                selectList={[{ id: 1, title: "Nhân viên kinh doanh", value: USER_TYPE.Sales }]}
                getSearchKeyword={setSaleSearch}
                autoComplete={{
                  [USER_TYPE.Sales]: {
                    endpoint: "/api/v1/users",
                    type: USER_TYPE.Sales,
                    state: setSaleSearch,
                    label: "Nhân viên kinh doanh",
                    placeholder: "Nhân viên kinh doanh",
                    sx: { width: "100%", borderRadius: "6px" },
                    limit: 20,
                    skip: 0,
                  },
                }}
              />
            </Box>
            <TextField
              placeholder="Khách hàng..."
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, borderRadius: "6px" }}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </Box>
          <Stack
            direction="row"
            spacing={2}
            flex={1}
            justifyContent="flex-end"
          >
            <Button
              disabled={!permissionCheck([PERMISSIONS.CREATE_CUSTOMER])}
              onClick={handleImport}
              variant="contained"
              startIcon={<FileUploadIcon />}
              color="info"
            >
              <Tooltip title="Tải mẫu danh sách">
                <span>Nhập danh sách</span>
              </Tooltip>
            </Button>
            <Button
              disabled={!permissionCheck([PERMISSIONS.VIEW_CUSTOMER])}
              onClick={handleExport}
              variant="contained"
              startIcon={<ExportIcon />}
            >
              Xuất danh sách
            </Button>
            <Button
              disabled={!permissionCheck([PERMISSIONS.CREATE_CUSTOMER])}
              onClick={handleDownloadTemplate}
              variant="outlined"
              startIcon={<ExportIcon />}
            >
              Tải mẫu
            </Button>
          </Stack>
        </Box>
        <Typography sx={{ fontWeight: "bold", mb: 1.5 }}>{total < 10 ? "0" + total : total} khách hàng </Typography>
      </Stack>

      <TableComp
        action={action}
        headers={header}
        bodyList={bodyList}
        sortData={sortData}
      />
    </Fragment>
  );
}
